import { useContext, useEffect, useState, useRef } from 'react'
import { useFormikContext, Field } from 'formik'
import * as Yup from 'yup'
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events'
import classnames from 'classnames/bind'
import { BasicModal, AddLocation, ButtonDrawer, FocusElement, Icon, LocationSearch, FormContent, Notifications, FormHeader, BookingSummary } from '~/components'
import MobileOverlay from '~/components/fields/LocationSearch/MobileOverlay'

const MOBILE_LOCATION_OVERLAY = false

import { StepperContext } from '~/components/ui/ViewStepper'
import { StoreContext } from '~/store'
import { wrapWithValidation, validateSetErrorsAndTouch, setTrue } from '~/utils'
import { useDidMountEffect } from '~/utils/hooks'
import { hasAvailability, isBookingAvailable, hasInformationTag, getMaxAllowedIntermediateStops, isEditMode, normalizePriceType } from '~/utils/data'
import * as styles from './index.module.css'
import * as common from '~/config/common.module.css'
import { motion, Reorder, useDragControls } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import i18next from '~/i18n'
import LocationItem from './modules/LocationItem'
import EditFieldsExpirationWarning from '../EditFieldsExpirationWarning'

const cx = classnames.bind(common)
const cxs = classnames.bind(styles)

const hasValue = value => value !== '' && value !== '-' && value?.address !== undefined

// Works out whether a destination is mandatory before the user can continue, using the
// loaded availability.direct response (the start page only fetches direct). A destination
// is needed when there are intermediate stops, when the trip rules say so, or when no
// usable price type exists for the current mode (e.g. taximeter is switched off so only
// fixed price remains, which cannot be priced without a destination).
// NOTE: gotoHow replicates the Yup destination.when check here because values.priceType
// may be stale from prebook when returning from SelectTime (the priceType effect doesn't
// re-fire on pickupTime change alone, only when values.availability changes).
const getDestinationRequirements = (values) => {
  const directAvail = values.availability?.direct
  const directLoaded = directAvail !== undefined && directAvail !== false
  const directFixed = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
  const directPriceTypeStr = directFixed ? 'FIXED' : 'TAXIMETER'
  const directRawPriceTypes = directLoaded ? (directAvail.priceAndPaymentRules?.priceTypes || []) : []
  const directPriceType = directRawPriceTypes.map(normalizePriceType).find(pt => pt.type === directPriceTypeStr) ?? false
  const tripRules = directAvail?.tripRules ?? values.availability?.prebook?.tripRules
  const hasStops = values.intermediateStopLocations?.length > 0
  const noUsablePriceType = directLoaded && (directPriceType === false || directPriceType?.paymentMethods?.length === 0)
  const destinationMissing = !values.destination?.address
  const directNeedsDestination = !!(hasStops || noUsablePriceType || tripRules?.directBookingRequiresDestination === true)
  const prebookNeedsDestination = !!(hasStops || noUsablePriceType || tripRules?.preBookingRequiresDestination === true)
  return {
    directPriceType,
    tripRules,
    destinationMissing,
    directNeedsDestination,
    prebookNeedsDestination,
    // Only trust the "missing" verdict once availability is loaded, otherwise the
    // buttons would flip state before we know anything about the trip
    directMissingDestination: directLoaded && directNeedsDestination && destinationMissing,
    prebookMissingDestination: directLoaded && prebookNeedsDestination && destinationMissing,
  }
}

const SelectLocations = ({fullscreen, currentView}) => {
  const { t } = useTranslation()
  const { next } = useContext(StepperContext)
  const { user } = useContext(StoreContext)
  const wrappedNext = wrapWithValidation(next, useFormikContext())
  const validateForm = validateSetErrorsAndTouch(useFormikContext())

  const { values, setFieldValue, errors, setErrors, setTouched, validateForm: formikValidateForm } = useFormikContext()
  const inEditMode = isEditMode(values)
  const [isAdding, setIsAdding] = useState(false)
  const maxAllowedIntermediateStops = getMaxAllowedIntermediateStops(values)
  const isAddEnabled = !isAdding && 
    values.departure?.address &&
    values.destination?.address &&
    maxAllowedIntermediateStops > 0 && 
    values.intermediateStopLocations?.length < maxAllowedIntermediateStops
  const isReorderEnabled = true; /*
    values.departure?.address ||
    values.destination?.address ||
    values.intermediateStopLocations?.length > 0*/
  const noDirectBooking = hasInformationTag(values, 'NO_DIRECT_BOOK');
  // When the backend cannot offer any cars *because* a destination is missing (e.g. only
  // fixed price is available), keep the buttons clickable so the click surfaces the red
  // "Destination krävs" validation instead of a dead "Inga tillgängliga bilar" state.
  const { directMissingDestination, prebookMissingDestination } = getDestinationRequirements(values)
  const isDirectBookDisabled = hasAvailability(values, 'direct') && !directMissingDestination ? !isBookingAvailable(values, 'direct') : false
  const noAvailabilities = !(values?.availability?.direct?.availabilities?.length > 0 || values?.availability?.prebook?.availabilities?.length > 0)
  const isPreebookDisabled = noAvailabilities && !prebookMissingDestination
  console.log("isPreebookDisabled", isPreebookDisabled, { noAvailabilities, directMissingDestination, prebookMissingDestination, hasDestination: !!values.destination?.address });
  const [isLaterClicked, setIsLaterClicked] = useState(false)
  const [isNextClicked, setIsNextClicked] = useState(false)

  const gotoNextLocationField = (currentFieldIndex) => {
    console.log("gotoNextLocationField", currentFieldIndex)
  }
  useCustomEventListener('GOTO_NEXT_LOCATION_FIELD', gotoNextLocationField)

  const intervalRef = useRef()

  const handleFocus = (event, location) => {
    const el = event.target.closest('li')
    if (el) {
      const setZindex = () => {
        el.style.zIndex = 2
      }
      clearInterval(intervalRef.current)
      intervalRef.current = setInterval(setZindex, 10)
      setZindex()
    }
    if (location?.address) {
      emitCustomEvent('ZOOM_LOCATION', location)
    }
    // Disable cover widget
    APP.trigger(APP.events.BOOKING_MODULE_DISABLE_HIDE_MAP)
  }

  const handleBlur = () => {
    clearInterval(intervalRef.current)
  }

  

  // NOTE: Reorder.Group needs a key to to be able to sort the items
  const getFields = (fullscreen) => {
    let fieldIndex = 0
    // console.log("vals", values);
    return [
      {
        // data: values.departure,
        key: values?.departure?.key ?  `reorder${values?.departure?.key}` : `reorder${fieldIndex}`,
        data: hasValue(values.departure) ? values.departure : { key: 'departure' },
        element: <Field name="departure" fieldIndex={fieldIndex} key={values?.departure?.key ? `field${values?.departure?.key}` : `field${fieldIndex}`} placeholder={t('placeholder-origin-location')} component={MOBILE_LOCATION_OVERLAY ? MobileOverlay : LocationSearch} fullscreen={fullscreen} onFocus={(e) => handleFocus(e, values.departure)} onBlur={handleBlur} useLocation={true} />
      },
      ...values.intermediateStopLocations.map((location, index) => {
        fieldIndex++
        return {
          // data: location,
          key: location?.key ?  `reorder${location?.key}` : `reorder${fieldIndex}`,
          data: hasValue(location) ? location : { key: `stop${index}` },
          element: <Field name={`intermediateStopLocations[${index}]`} parentLocation={values?.intermediateStopLocations?.[index - 1] || values?.departure} fieldIndex={fieldIndex} key={location?.key ?  `field${location?.key}` : `field${fieldIndex}`} placeholder={t('placeholder-add-stop')} component={MOBILE_LOCATION_OVERLAY ? MobileOverlay : LocationSearch} fullscreen={fullscreen} onFocus={(e) => handleFocus(e, location)} onBlur={handleBlur} />
        }
      }),
      {
        // data: values.destination,
        key: values?.destination?.key ?  `reorder${values?.destination?.key}` : `reorder${fieldIndex + 1}`,
        
        data: hasValue(values.destination) ? values.destination : { key: 'destination' },
        element: <Field name="destination" parentLocation={values?.intermediateStopLocations?.length > 0 ? values?.intermediateStopLocations[values?.intermediateStopLocations?.length - 1] : values?.departure} fieldIndex={fieldIndex + 1} key={values?.destination?.key ? `field${values?.destination?.key}` : `field${fieldIndex + 1}`} placeholder={t('placeholder-destination-location')} component={MOBILE_LOCATION_OVERLAY ? MobileOverlay : LocationSearch} fullscreen={fullscreen} onFocus={(e) => handleFocus(e, values.destination)} onBlur={handleBlur} />
      }
    ]
  }

  const [fields, setFields] = useState(getFields(fullscreen))
  const [draggable, setDraggable] = useState(false)
  const ref = useRef()

  const gotoTime = () => {
    console.log('[SelectLocations] gotoTime clicked — availabilityIsLoading:', values.availabilityIsLoading, 'errors:', errors)
    if (values.availabilityIsLoading) {
      setIsLaterClicked(true)
      return
    }
    formikValidateForm().then((freshErrors) => {
      const { prebookMissingDestination } = getDestinationRequirements(values)
      const errorsToShow = { ...freshErrors }
      if (prebookMissingDestination) {
        errorsToShow.destination = i18next.t('validation-destination-required')
      }
      const hasBlockingError = !!errorsToShow.departure ||
        !!errorsToShow.destination ||
        !!errorsToShow.intermediateStopLocations
      console.log('[SelectLocations] gotoTime — prebookMissingDestination:', prebookMissingDestination, 'hasBlockingError:', hasBlockingError, 'errorsToShow:', errorsToShow)
      if (hasBlockingError) {
        setErrors(errorsToShow)
        setTouched(setTrue(errorsToShow), false)
      } else {
        next('time')
      }
    })
  }

  const gotoHow = () => {
    console.log('[SelectLocations] gotoHow clicked — availabilityIsLoading:', values.availabilityIsLoading, 'errors:', errors, 'priceType:', values.priceType)
    if (values.availabilityIsLoading) {
      setIsNextClicked(true)
      return
    }
    setFieldValue('pickupTime', '')
    // Clear flight details — only valid for prebook airport pickups
    setFieldValue('flightNumber', '', false)
    setFieldValue('checkedInBaggage', false, false)
    // Use cached errors directly — async re-validation can return clean due to
    // timing issues, so we apply the already-computed errors and touch them directly.
    if (Object.keys(errors).length > 0) {
      console.log('[SelectLocations] gotoHow → has errors, surfacing:', errors)
      window.AUTO_FOCUS_FIELD = null
      setErrors(errors)
      setTouched(setTrue(errors), false)
      return
    }
    // Replicate the Yup destination.when check for DIRECT mode using availability.direct
    // (see getDestinationRequirements for why values.priceType can't be trusted here).
    const { directPriceType, directNeedsDestination, tripRules } = getDestinationRequirements(values)
    console.log('[SelectLocations] gotoHow → direct dest check:', {
      directPriceType,
      directNeedsDestination,
      directBookingRequiresDestination: tripRules?.directBookingRequiresDestination,
      destinationAddress: values.destination?.address,
    })
    if (directNeedsDestination && !values.destination?.address) {
      console.log('[SelectLocations] gotoHow → blocked: destination required for direct mode')
      const destError = { destination: i18next.t('validation-destination-required') }
      window.AUTO_FOCUS_FIELD = null
      setErrors(destError)
      setTouched({ destination: true }, false)
      return
    }
    if (hasAvailability(values, 'direct') && isBookingAvailable(values, 'direct')) {
      console.log('[SelectLocations] gotoHow → wrappedNext(how)')
      wrappedNext('how')
    } else {
      console.log('[SelectLocations] gotoHow → direct not available, validating')
      validateForm()
    }
  }

  const buttons = [
    {
      func: gotoTime,
      title: t('button-go-later'),
      isPrimary: noDirectBooking || isDirectBookDisabled,
      disabled: isPreebookDisabled,
      isLoading: isLaterClicked && values.availabilityIsLoading
    },
    {
      func: gotoHow,
      title: isDirectBookDisabled ? t('button-no-available-cars') : t('button-go-now'),
      disabled: noDirectBooking || isDirectBookDisabled,
      isLoading: (isNextClicked || isDirectBookDisabled) && values.availabilityIsLoading
    },
  ]
  console.log("buttons -- ", buttons);

  const showAddStop = () => {
    setIsAdding(true)
  }

  const handleAddLocation = location => {
    console.log("handleAddLocation", location);
    // console.log("values", values);
    setFieldValue('intermediateStopLocations', [...values.intermediateStopLocations, values.destination])
    setFieldValue('destination', location)
    setIsAdding(false)
    setTimeout(() => validateForm(), 10)
    // setTimeout(() => {updateValues(fields)}, 1000);
  }

  const handleRemove = index => {
    console.log("handleRemove", index);
    if (typeof index === 'number') {
      //fields[index].element.props.component.reset();
      console.log(fields[index]);
      emitCustomEvent('CLEAR_LOCATION_SEARCH', fields[index]?.element?.props?.name);
      if (fields.length > 2) {
        const items = fields.filter((v, i) => i !== index)
        setFields(items)
        updateValues(items)
      }
      else {
        console.log("handleRemove", "removing last item", fields);
        setFieldValue(index === 0 ? 'departure' : 'destination', undefined)
      }
    }
  }

  const handleReorder = items => {
    const allowFlightNumber = items[0]?.data?.locationInformation?.map(item => item.tag).includes('FLIGHT_NBR');
    if(!allowFlightNumber) {
      setFieldValue('flightNumber', '');
      setFieldValue('checkedInBaggage', undefined);
    }
    console.log("handleReorder", items[0]?.data?.locationInformation);
    //setFieldValue('flightNumber', value.toUpperCase()); checkedInBaggage
    setFields(items)
  }

  const handleDragStart = e => {
    const el = e.target.closest('.' + styles.row)
    if (el) el.classList.add(styles.dragging)
  }

  const handleDragEnd = e => {
    //const el = e.target.closest('.' + styles.row)
    //if (el) el.classList.remove(styles.dragging)
    //setDraggable(false)
    updateValues(fields)
  }

  const updateValues = (items) => {
    console.log("updateValues");
    // setFieldValue('departure', items[0]?.data || '')
    // setFieldValue('destination', items[items.length - 1]?.data || '')
    setFieldValue('departure', items[0]?.data?.address ? items[0].data : '')
    setFieldValue('destination', items[items.length - 1]?.data?.address ? items[items.length - 1].data : '')

    if (items.length > 2) {
      const stops = items.slice(1, -1).map(item => item?.data?.address ? item.data : '')
      setFieldValue('intermediateStopLocations', stops)
    }
    else {
      setFieldValue('intermediateStopLocations', [])
    }

    setTimeout(() => validateForm(), 1)
  }

  useDidMountEffect(() => {
    if (!values.availabilityIsLoading) {
      if (isNextClicked) {
        setIsNextClicked(false)
        console.log('[SelectLocations] availability loaded, isNextClicked → gotoHow (isDirectBookDisabled:', isDirectBookDisabled, ')')
        const { directMissingDestination } = getDestinationRequirements(values)
        if (directMissingDestination) {
          // The response says a destination is required - show that instead of validating
          // against values.priceType, which may not have been updated by the Fetcher yet
          window.AUTO_FOCUS_FIELD = null
          setErrors({ destination: i18next.t('validation-destination-required') })
          setTouched({ destination: true }, false)
        } else if (hasAvailability(values, 'direct') && !isDirectBookDisabled) {
          setTimeout(() => { wrappedNext('how') }, 500)
        } else {
          validateForm()
        }
      } else if (isLaterClicked) {
        setIsLaterClicked(false)
        console.log('[SelectLocations] availability loaded, isLaterClicked → gotoTime logic')
        formikValidateForm().then((freshErrors) => {
          const { prebookMissingDestination } = getDestinationRequirements(values)
          const errorsToShow = { ...freshErrors }
          if (prebookMissingDestination) {
            errorsToShow.destination = i18next.t('validation-destination-required')
          }
          const hasBlockingError = !!errorsToShow.departure ||
            !!errorsToShow.destination ||
            !!errorsToShow.intermediateStopLocations
          if (hasBlockingError) {
            console.log('[SelectLocations] blocking errors found:', errorsToShow)
            setErrors(errorsToShow)
            setTouched(setTrue(errorsToShow), false)
          } else {
            setTimeout(() => { next('time') }, 500)
          }
        })
      }
    }
  }, [values.availability, values.availabilityIsLoading])

  useEffect(() => {
    APP.trigger(APP.events.BOOKING_MODULE_ENABLE_HIDE_MAP)
  }, [])

  useEffect(() => {
    setFields(getFields(fullscreen))
  }, [values.departure, values.destination, values.intermediateStopLocations])
    {/* <motion.div layout transition={{duration:.18}} className={cx(common.widgetWrapper, { tabsLeft: true })}> */}
  return (
    <div className={cx(common.widgetWrapper, { fullscreen, tabsLeft: true })}>
      {fullscreen && <>
        <FormContent>
          <FormHeader onlyFullscreen>{inEditMode ? t('edit-your-trip') : t('your-trip')}</FormHeader>
          <FormHeader onlyMobile>{t('header-where-do-you-want-to-go')}</FormHeader>
          <Notifications fullscreen={fullscreen} showBothBookingTypes />
          <BookingSummary values={values} currentView={currentView} />
        </FormContent>
        
      </>}
      {!fullscreen && <Notifications showBothBookingTypes />}
      <FocusElement />

      <div className={fullscreen ? common.contentBottom : null}>
      <EditFieldsExpirationWarning />
      <FormContent noStyle={fullscreen}>
        {fullscreen && <FormHeader onlyFullscreen>{t('header-where-do-you-want-to-go')}</FormHeader>}
        {/* <FormHeader className={styles.header}>{t('header-book-taxi')}</FormHeader> */}
        {/* 
            <Reorder.Item 
              key={item?.data?.key||index} 
              layout="position"
              value={item} 
              dragListener={false}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              dragConstraints={ref}
              dragElastic={0.05}
              className={styles.row}
            >
              <div className={styles.field}>
                {item.element}
                <div className={cxs(styles.dragHandle, { enabled: isReorderEnabled })}
                  onMouseEnter={() => setDraggable(true)}
                  onMouseLeave={() => setDraggable(false)} // retain this for better animation
                  onTouchStart={() => setDraggable(true)} // for mobile: need to set draggable to `false` in `onDragEnd` prop, not `onTouchEnd`
                >
                  <Icon id="drag" />
                </div>
              </div>
              <button onClick={() => handleRemove(index)} className={styles.removeButton} tabIndex="-1">
                <div className={styles.removeBackground}>
                  <Icon id="plus" />
                </div>
              </button>
            </Reorder.Item>
            */}
        <Reorder.Group axis="y" values={fields} onReorder={handleReorder} ref={ref} className={styles.locations}>
          {fields.map((item, index) => {return (
            <LocationItem 
              //handleItemDragStart={}
              previusItem={fields[index - 1] || null}
              handleRemove={handleRemove}
              handleItemDragEnd={handleDragEnd}
              reorderKey={item?.key||index} 
              key={item?.key||index} 
              dragConstraints={ref}
              item = {item} 
              isReorderEnabled={isReorderEnabled}
            />
          )})}
        </Reorder.Group>
        {isAdding && <div className={styles.addLocationForm}><AddLocation parentLocation={values?.destination} region={values.region} onLocationSelected={handleAddLocation} /></div>}
        {maxAllowedIntermediateStops > 0 && <button className={styles.addLocationButton} onClick={showAddStop} disabled={!isAddEnabled} tabIndex="0">{t('button-add-stop')}</button>}
        {user === null && <div className={styles.text}>{t('booking-login-info')}</div>}
      </FormContent>
      </div>
      <ButtonDrawer buttons={buttons} fullscreen={fullscreen}/>
    </div>
  )
}

Yup.addMethod(Yup.object, 'validStop', function(errorMessage) {
  return this.test('test-valid-stop', errorMessage, function(value) {
    const { path, createError } = this
    return (
      value?.pointOfInterestType !== 'AIRPORT' || createError({ path, message: errorMessage })
    )
  })
})

Yup.addMethod(Yup.object, 'validDepature', function(errorMessage) {
  return this.test('test-valid-depature', errorMessage, function(value) {
    console.log('validate depature', value);
    const { path, createError } = this
    if(value?.bookable === false) return createError({ path, message: errorMessage });
    return (
      !value || value?.locationInformation?.filter(info => ['NO_PICKUP'].includes(info?.tag)).length === 0 || createError({ path, message: errorMessage })
      //true //value?.pointOfInterestType !== 'AIRPORT' || createError({ path, message: errorMessage })
    )
  })
})

Yup.addMethod(Yup.object, 'validDestination', function(errorMessage) {
  return this.test('test-valid-destination', errorMessage, function(value) {
    console.log('validate destination', value);
    const { path, createError } = this
    if(value?.bookable === false) return createError({ path, message: errorMessage });
    return (
      !value || value?.locationInformation?.filter(info => ['NO_DROP_OFF'].includes(info?.tag)).length === 0 || createError({ path, message: errorMessage })
      //true //value?.pointOfInterestType !== 'AIRPORT' || createError({ path, message: errorMessage })
    )
  })
})

Yup.addMethod(Yup.object, 'validIntermediate', function(errorMessage) {
  return this.test('test-valid-intermediate', errorMessage, function(value) {
    console.log('validate intermediate', value);
    const { path, createError } = this
    if(value?.bookable === false) return createError({ path, message: errorMessage });
    return (
      value?.locationInformation?.filter(info => ['NO_INTERMEDIATE_STOP', 'NO_DROP_OFF', 'NO_PICKUP'].includes(info?.tag)).length === 0 || createError({ path, message: errorMessage })
      //createError({ path, message: errorMessage })
      //true //value?.pointOfInterestType !== 'AIRPORT' || createError({ path, message: errorMessage })
    )
  })
})

SelectLocations.validationSchema = Yup.object({
  // TODO: This does not work when loading JSON from the server...
  departure: Yup.object().nullable().validDepature(i18next.t('validation-departure-invalid')).required(i18next.t('validation-departure-required')),
  destination: Yup.object()
    .when(['intermediateStopLocations', 'priceType', 'pickupTime', 'availability'], (intermediateStopLocations, priceType, pickupTime, availability) => {
      const key = pickupTime === '' ? 'direct' : 'prebook'
      const tripRules = availability?.[key]?.tripRules
      const requiresDestination = key === 'direct'
        ? tripRules?.directBookingRequiresDestination === true
        : tripRules?.preBookingRequiresDestination === true
      const required = !!(intermediateStopLocations?.length > 0 ||
        priceType === false ||
        priceType?.paymentMethods?.length === 0 ||
        requiresDestination)
      console.log('[Yup destination.when] priceType:', priceType, '| requiresDestination:', requiresDestination, '| key:', key, '| required:', required)
      return required ?
        Yup.object().nullable().validDestination(i18next.t('validation-destination-invalid')).required(i18next.t('validation-destination-required')) :
        Yup.object().nullable().validDestination(i18next.t('validation-destination-invalid'))
    }),
  intermediateStopLocations: Yup.array().of(
    Yup.object().nullable().required(i18next.t('validation-destination-required')).validIntermediate(i18next.t('validation-intermediate-invalid'))
  )
})

export default SelectLocations
